import { Avatar, cn } from '@ludus/ui'
import { useTranslation } from 'react-i18next'
import { managerDisplayName } from '@ludus/core'
import { mockManagers, type MockManager } from '@/shared/mocks/managers.ts'
import type { ManagerId } from '../../types.ts'

interface ManagerPickerProps {
  selectedId: ManagerId | null
  onSelect: (id: ManagerId) => void
}

export function ManagerPicker({ selectedId, onSelect }: ManagerPickerProps) {
  const { t } = useTranslation()
  return (
    <div className="flex flex-col gap-1.5 px-2 pt-2">
      <span className="px-1 text-xs font-semibold uppercase tracking-wide text-muted">
        {t('pages.calendar.sidebar.managers')}
      </span>
      <ul className="flex flex-col gap-0.5">
        {mockManagers.map((manager) => (
          <li key={manager.id}>
            <ManagerRow
              manager={manager}
              selected={selectedId === manager.id}
              onClick={() => onSelect(manager.id)}
            />
          </li>
        ))}
      </ul>
    </div>
  )
}

function ManagerRow({
  manager,
  selected,
  onClick,
}: {
  manager: MockManager
  selected: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={selected}
      className={cn(
        'group flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm',
        'outline-none transition-colors',
        'focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background',
        selected
          ? 'bg-sidebar-active-bg text-sidebar-active-text'
          : 'text-sidebar-text hover:bg-sidebar-hover-bg hover:text-sidebar-active-text',
      )}
    >
      <Avatar
        size="sm"
        initials={manager.initials}
        color={manager.color}
        aria-label={managerDisplayName(manager)}
      />
      <span className="truncate">{managerDisplayName(manager)}</span>
      {!manager.isActive && (
        <span className="ml-auto rounded-full border border-border/60 px-1.5 text-[10px] uppercase text-muted">
          off
        </span>
      )}
    </button>
  )
}
